import {
  ApiBlueprint,
  DiscoveryApi,
  FetchApi,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';

/** Mirrors AccessReport in plugins/azure-access-backend. */
export type AccessRow = {
  role: string;
  privileged: boolean;
  scope: { kind: string; label: string; entityRef?: string };
  principal: { id: string; type: string; label: string; entityRef?: string };
  inherited: boolean;
};

export type AccessReport = {
  subject: string;
  targetKind: 'principal' | 'resourceGroups';
  rows: AccessRow[];
  notes: string[];
  generatedAt: string;
};

export interface AzureAccessApi {
  /** The signed-in user's own Azure access. */
  getMine(): Promise<AccessReport>;
  /** The Azure access of an entity; the backend answers 403 to non-admins. */
  getForEntity(entityRef: string): Promise<AccessReport>;
}

export const azureAccessApiRef = createApiRef<AzureAccessApi>().with({
  id: 'plugin.azure-access.api',
  pluginId: 'azure-access',
});

export class AzureAccessClient implements AzureAccessApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  getMine(): Promise<AccessReport> {
    return this.get('/access/me');
  }

  getForEntity(entityRef: string): Promise<AccessReport> {
    return this.get(`/access?entityRef=${encodeURIComponent(entityRef)}`);
  }

  private async get(path: string): Promise<AccessReport> {
    const baseUrl = await this.discoveryApi.getBaseUrl('azure-access');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }
}

export const azureAccessApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: azureAccessApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new AzureAccessClient(discoveryApi, fetchApi),
    }),
});
